import React from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { Button } from '../../components';
import {
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useGetTenantIntegrations } from '../../apis/tenant-integrations';

import { useTranslation, Trans } from 'react-i18next';

interface GitLabSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const GitLabSkillSetup: React.FC<GitLabSkillSetupProps> = ({
  skill,
  onCancel,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetTenantIntegrations();

  const hasEnabledIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'gitlab' && integration.enabled,
    );
  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          GitLab
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-green-50 blur-xl dark:bg-green-950/10" />
            {skill.image ? (
              skill.image.src ? (
                <Image
                  src={skill.image.src}
                  alt={skill.image.alt}
                  className="relative h-20 rounded-xl object-cover"
                />
              ) : (
                <Image
                  name={skill.image.name}
                  type={skill.image.type}
                  className="relative h-20 rounded-xl object-cover"
                  fileExtension={skill.image.fileExtension}
                />
              )
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iSearchAndFixIssuesInYourGitLab')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/gitlab"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Integration Status */}
        <div className="mb-8 rounded-lg border p-4">
          {isLoadingIntegrations ? (
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600" />
              <div>
                <p className="font-medium">{t('checkingIntegrationStatus')}</p>
                <p className="text-sm text-gray-500">
                  {t('pleaseWaitWhileWeCheckYourGitLab')}
                </p>
              </div>
            </div>
          ) : hasEnabledIntegration ? (
            <div className="flex items-center gap-3 text-green-700">
              <CheckIcon className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">{t('gitLabIntegrationActive')}</p>
                <p className="text-sm text-green-600">
                  {t('yourGitLabIntegrationIsAlreadyConfiguredAnd')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-amber-700">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium">{t('gitLabIntegrationRequired')}</p>
                <p className="text-sm text-amber-600">
                  <Trans
                    t={t}
                    i18nKey="pleaseConfigureYourGitLabIntegrationFirstTo"
                    components={{
                      a: (
                        <a
                          href="settings/integrations/add/GitLab/GitLab"
                          className="text-blue-600 underline hover:text-blue-800"
                        />
                      ),
                    }}
                  />
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 6. Call to Action Button */}
        <div className="mb-6 flex flex-col items-center">
          <Button onClick={onCancel} variant="secondary" className="px-8 py-3">
            {t('goBack')}
          </Button>
        </div>
      </div>
    </div>
  );
};
